import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { EdgeConfig, EdgeMode } from "../lib/edge/edgeState";

/**
 * Локалният резерв („edge", docs/edge.md) — състоянието за UI-я: лентата
 * „Локален режим" над Маркиране/Мониторите, редът „Локален резерв" в
 * Конфигурация → Устройства, disabled-ването на функциите извън обхвата,
 * интервалът на `useBills`.
 *
 * Slice-ът е чист (без странични ефекти). ЕДИНСТВЕНИЯТ писач е
 * `lib/edge/edgeMode.ts`, който държи в синхрон и `lib/edge/edgeState.ts`
 * (синхронният източник за axios интерсептора и WS-а) — не dispatch-вай
 * тези действия от компонент.
 */
/**
 * Състоянието на ВРЪЗКАТА — шест положения вместо „има или няма интернет",
 * защото трите повреди искат три различни отговора към сервитьора
 * (`lib/edge/edgeMode.ts` решава кое е в сила):
 *
 *  • `online`          всичко е наред;
 *  • `slow`            заявка мълчи над 1,5 s. Още не е авария;
 *  • `suspect`         сондата се провали и се пита касата;
 *  • `device_offline`  касата вижда облака ЖИВ — проблемът е само в ТОВА
 *                      устройство. Обектът НЕ се замразява;
 *  • `app_down`        и терминалът, и касата виждат жива мрежа и мъртво
 *                      приложение — почти винаги наш деплой, чака се;
 *  • `local`           замразено, работи се срещу касата.
 */
export type EdgeLinkState =
  | "online"
  | "slow"
  | "suspect"
  | "device_offline"
  | "app_down"
  | "local";

export interface EdgeSliceState {
  /** Резервът е включен в облака (`GET /configuration/edge`). */
  enabled: boolean;
  ip: string | null;
  port: number;
  masterClientId: string | null;
  /** Режимът, в който ОБЛАКЪТ смята ресторанта (fence на BE). */
  cloudMode: EdgeMode;
  /** Режимът на ТОЗИ терминал — срещу облака или срещу касата. */
  mode: EdgeMode;
  /**
   * Локален режим БЕЗ билет (§14.3.1): касата приема само двата login-а, а
   * всичко останало води до екрана за вход. `false` в облака и в пълния
   * локален режим.
   */
  restricted: boolean;
  /** Стига ли браузърът до касата (`GET {edge}/health`); null = непроверено. */
  edgeReachable: boolean | null;
  /** Браузърът е на самата каса и ходи до нея през `127.0.0.1`
   * (`isThisDeviceTheTill`, `lib/edge/edgeLoopback.ts`). */
  thisDeviceIsTill: boolean;
  /** Последната сонда към облака (само докато сондите текат). */
  cloudReachable: boolean;
  /** В локален режим: касата е изпратила дневника си (връщането чака това). */
  synced: boolean;
  /**
   * Действия, които облакът е ОТКАЗАЛ и които касата не може да качи сама —
   * чакат човек при нея (§17.1 на договора). Лентата ги показва като
   * предупреждение, а връщането в облака ги чака. 0 в облак режим.
   */
  unresolved: number;
  /**
   * Касата е задържана в локален режим по РЪЧНО решение на човек (§19.5) и
   * няма да се върне сама. Лентата го казва и дава „Върни към облака".
   * `false` в облак режим и при по-стара каса.
   */
  manualOverride: boolean;
  /** От кога тече локалният режим (ISO от касата). */
  localSince: string | null;
  /** Състоянието на връзката (виж `EdgeLinkState`). Пише се САМО от
   * `lib/edge/edgeMode.ts`; UI-ят го чете. */
  linkState: EdgeLinkState;
  /**
   * Какво да се каже на човека за това състояние — i18n КЛЮЧ (`edge.link*`),
   * не готов текст: терминалът може да е на английски, а ключът се превежда
   * при рендера. `null` = няма какво да се казва (всичко е наред).
   */
  linkMessage: string | null;
  /**
   * Касата сервира „Резервации" и „Настани" без интернет — `GET {edge}/health`
   * е казала `capabilities.reservations === 1` (пише го
   * `lib/edge/edgeSession.ts`, при всяка сонда за достъпност). По-стара каса
   * или каса без миграцията в облака → `false` и модулът остава заключен в
   * локален режим, както досега.
   */
  reservationsOffline: boolean;
  /**
   * В локален режим: към кой момент са данните за резервациите на касата
   * (`reservations_as_of` от `GET {edge}/sync-state`, ISO на касата). `null`
   * в облака и при каса без резервации.
   */
  reservationsAsOf: string | null;
  /**
   * Касата приема бакшиш при закриване без интернет — `GET {edge}/health`
   * е казала `capabilities.tips === 1` (пише го `lib/edge/edgeSession.ts`
   * при всяка сонда, като `reservationsOffline`). Без нея в локален режим
   * секцията „Бакшиш" в „Закрий" е заключена: по-стара каса тихо би
   * пренебрегнала полетата и би закрила без бакшиша.
   */
  tipsOffline: boolean;
  /**
   * Касата приема отстъпката в € на цялата сметка без интернет — `GET
   * {edge}/health` е казала `capabilities.bill_discount === 1` (пише го
   * `lib/edge/edgeSession.ts`, като `tipsOffline`). Без нея в локален режим
   * табът „На цялата сметка" и „Закрий" за сметка с такава отстъпка са
   * заключени: по-стара каса би закрила по пълни цени.
   */
  billDiscountOffline: boolean;
}

const initialState: EdgeSliceState = {
  enabled: false,
  ip: null,
  port: 8787,
  masterClientId: null,
  cloudMode: "cloud",
  mode: "cloud",
  restricted: false,
  edgeReachable: null,
  thisDeviceIsTill: false,
  cloudReachable: true,
  synced: true,
  unresolved: 0,
  manualOverride: false,
  localSince: null,
  linkState: "online",
  linkMessage: null,
  reservationsOffline: false,
  reservationsAsOf: null,
  tipsOffline: false,
  billDiscountOffline: false,
};

const edgeSlice = createSlice({
  name: "edge",
  initialState,
  reducers: {
    setEdgeConfig(state, action: PayloadAction<EdgeConfig | null>) {
      const config = action.payload;
      state.enabled = config?.enabled ?? false;
      state.ip = config?.ip ?? null;
      state.port = config?.port ?? 8787;
      state.masterClientId = config?.masterClientId ?? null;
      state.cloudMode = config?.cloudMode ?? "cloud";
      // Изключен резерв няма какво да е „достъпен" — състоянието се
      // проверява наново при следващо включване.
      if (!state.enabled) {
        state.edgeReachable = null;
        state.reservationsOffline = false;
        state.tipsOffline = false;
        state.billDiscountOffline = false;
      }
    },
    setEdgeMode(
      state,
      action: PayloadAction<{ mode: EdgeMode; localSince: string | null }>,
    ) {
      state.mode = action.payload.mode;
      state.localSince =
        action.payload.mode === "local" ? action.payload.localSince : null;
      if (action.payload.mode === "cloud") {
        state.synced = true;
        state.restricted = false;
        // В облака никой не пита касата — последният брой е стар (и излизаме
        // с неразрешени само когато тя е недостижима, §17.1).
        state.unresolved = 0;
        state.manualOverride = false;
        state.reservationsAsOf = null;
      }
    },
    setEdgeRestricted(state, action: PayloadAction<boolean>) {
      state.restricted = action.payload;
    },
    setEdgeReachable(state, action: PayloadAction<boolean | null>) {
      state.edgeReachable = action.payload;
    },
    setThisDeviceIsTill(state, action: PayloadAction<boolean>) {
      state.thisDeviceIsTill = action.payload;
    },
    setCloudReachable(state, action: PayloadAction<boolean>) {
      state.cloudReachable = action.payload;
    },
    setEdgeSynced(state, action: PayloadAction<boolean>) {
      state.synced = action.payload;
    },
    setEdgeUnresolved(state, action: PayloadAction<number>) {
      state.unresolved = action.payload;
    },
    setEdgeManualOverride(state, action: PayloadAction<boolean>) {
      state.manualOverride = action.payload;
    },
    setReservationsOffline(state, action: PayloadAction<boolean>) {
      state.reservationsOffline = action.payload;
    },
    setEdgeReservationsAsOf(state, action: PayloadAction<string | null>) {
      state.reservationsAsOf = action.payload;
    },
    setTipsOffline(state, action: PayloadAction<boolean>) {
      state.tipsOffline = action.payload;
    },
    setBillDiscountOffline(state, action: PayloadAction<boolean>) {
      state.billDiscountOffline = action.payload;
    },
    setEdgeLinkState(
      state,
      action: PayloadAction<{ state: EdgeLinkState; message: string | null }>,
    ) {
      state.linkState = action.payload.state;
      state.linkMessage = action.payload.message;
    },
  },
});

export const {
  setEdgeConfig,
  setEdgeMode,
  setEdgeRestricted,
  setEdgeReachable,
  setThisDeviceIsTill,
  setCloudReachable,
  setEdgeSynced,
  setEdgeUnresolved,
  setEdgeManualOverride,
  setEdgeLinkState,
  setReservationsOffline,
  setEdgeReservationsAsOf,
  setTipsOffline,
  setBillDiscountOffline,
} = edgeSlice.actions;

export default edgeSlice.reducer;
